import type Phaser from 'phaser';
import { keyLabel, type ActionInfo, type BindingStore } from './settings-core';

// The key-bindings table of the Controls page: one row per action, click
// the key to rebind, Esc cancels, reserved keys are refused, a reset
// button. Game-agnostic: the game supplies its actions and store. The
// capture-phase listener only acts while `isActive()` (the page is shown),
// so Phaser never sees a rebinding press as game input.
export interface BindingsTableConfig<A extends string> {
  game: Phaser.Game;
  table: HTMLTableElement;
  note: HTMLElement;
  resetButton: HTMLButtonElement;
  actions: ReadonlyArray<ActionInfo<A>>;
  store: BindingStore<A>;
  reserved: ReadonlySet<number>;
  isActive: () => boolean;
}

export interface BindingsTable {
  render(): void; // call when the page opens
  cancel(): void; // stop listening for a key (page closed)
}

export function installBindingsTable<A extends string>(cfg: BindingsTableConfig<A>): BindingsTable {
  let listeningFor: A | null = null;

  function render(): void {
    cfg.table.replaceChildren();
    for (const { action, name, help } of cfg.actions) {
      const tr = document.createElement('tr');
      const tdKey = document.createElement('td');
      tdKey.className = 'key';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'key' + (listeningFor === action ? ' listening' : '');
      btn.textContent = listeningFor === action ? 'press…' : cfg.store.bindings[action].label;
      btn.addEventListener('click', () => {
        listeningFor = listeningFor === action ? null : action;
        cfg.note.textContent = listeningFor ? `Press a key for "${name}"` : '';
        render();
      });
      tdKey.append(btn);
      const tdName = document.createElement('td');
      tdName.textContent = name;
      const tdHelp = document.createElement('td');
      tdHelp.className = 'help';
      tdHelp.textContent = help;
      tr.append(tdKey, tdName, tdHelp);
      cfg.table.append(tr);
    }
  }

  window.addEventListener(
    'keydown',
    (ev) => {
      if (!listeningFor || !cfg.isActive()) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      const action = listeningFor;
      if (ev.key === 'Escape') {
        listeningFor = null;
        cfg.note.textContent = '';
        // keyCode is deprecated but is what Phaser's Key objects are keyed by.
        // eslint-disable-next-line @typescript-eslint/no-deprecated
      } else if (cfg.reserved.has(ev.keyCode) || ev.key === 'Tab') {
        cfg.note.textContent = `${keyLabel(ev)} is reserved. Press another key.`;
        return;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        cfg.store.set(action, { code: ev.keyCode, label: keyLabel(ev) });
        listeningFor = null;
        cfg.note.textContent = '';
      }
      render();
    },
    true,
  );
  cfg.resetButton.addEventListener('click', () => {
    cfg.store.reset();
    listeningFor = null;
    cfg.note.textContent = 'Controls reset to defaults.';
    render();
  });

  return {
    render,
    cancel() {
      listeningFor = null;
      cfg.note.textContent = '';
    },
  };
}
